import {
  mergeRow,
  rowHlc,
  type Id,
  type LwwRow,
  type PullResponse,
  type PushResponse,
} from '@odhaczka/core';
import { getDeviceId, observe } from '../db/clock';
import { db, getMeta, lwwTable, setMeta, wipeLocalData } from '../db/db';

export interface SyncStatus {
  online: boolean;
  syncing: boolean;
  pending: number;
  lastSyncMs: number | null;
  error: string | null;
}

const PUSH_BATCH = 200;
const INTERVAL_MS = 30_000;
/** Po 409 household_changed: chwila na dokończenie scalenia na serwerze, potem jedna ponowna próba. */
const HOUSEHOLD_RETRY_MS = 1500;

class UnauthorizedError extends Error {}
class HouseholdChangedError extends Error {}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const JOINING_KEY = 'joiningHousehold';
/** Dołączenie, na które czekamy bez znanego jeszcze id domu (odpowiedź w drodze), uznajemy tylko przez chwilę. */
const JOINING_PENDING_MS = 2 * 60_000;

interface Joining {
  householdId: Id | null;
  sinceMs: number;
}

/** joinHousehold zapowiada zmianę domu (null = id jeszcze nieznane), żeby silnik nie skasował danych. */
export async function expectHouseholdChange(householdId: Id | null): Promise<void> {
  await setMeta(JOINING_KEY, { householdId, sinceMs: Date.now() } satisfies Joining);
}

export async function clearExpectedHousehold(): Promise<void> {
  await db.meta.delete(JOINING_KEY);
}

/**
 * Zmiana gospodarstwa; wołać w transakcji obejmującej wszystkie tabele.
 * - Dołączenie do domu (zapowiedziane przez joinHousehold) albo pierwsze logowanie: dane lokalne są podzbiorem
 *   nowego domu — zostają, a pełny pull łączy się z nimi przez mergeRow (outbox trafi do nowego domu).
 * - Każda inna zmiana (opuszczenie domu, usunięcie przez domownika): nowy dom jest pusty, a lokalne dane
 *   i outbox należą do starego (ich id dałyby konflikt) — kasujemy je.
 * W obu przypadkach kursor od zera.
 */
export const HOUSEHOLD_NOTICE_KEY = 'householdNotice';

async function switchHousehold(householdId: Id): Promise<'same' | 'kept' | 'wiped'> {
  const current = await getMeta<string>('householdId');
  if (current === householdId) return 'same';
  const joining = await getMeta<Joining>(JOINING_KEY);
  const expected =
    !!joining && (joining.householdId === householdId || (joining.householdId === null && Date.now() - joining.sinceMs < JOINING_PENDING_MS));
  let result: 'kept' | 'wiped' = 'kept';
  if (expected) await db.meta.delete(JOINING_KEY);
  else if (current !== undefined) {
    await wipeLocalData(['userId']);
    // Pokażemy wyjaśnienie zamiast pustego ekranu (leaveHousehold czyści dane sam, więc tu trafia usunięcie przez domownika).
    await setMeta(HOUSEHOLD_NOTICE_KEY, 'removed');
    result = 'wiped';
  }
  await setMeta('householdId', householdId);
  await setMeta('cursor', 0);
  return result;
}

/** Przyjmuje gospodarstwo z serwera (sesja, odpowiedź pushu). Zwraca true, jeśli się zmieniło. */
export async function adoptHousehold(householdId: Id): Promise<boolean> {
  const result = await db.transaction('rw', db.tables, () => switchHousehold(householdId));
  if (result === 'same') return false;
  syncEngine.householdChanged(householdId);
  return true;
}

class SyncEngine {
  private status: SyncStatus = {
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    syncing: false,
    pending: 0,
    lastSyncMs: null,
    error: null,
  };
  private listeners = new Set<(s: SyncStatus) => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running: Promise<void> | null = null;
  private again = false;
  private started = false;
  onUnauthorized: () => void = () => {};
  /** Wołane po udanej synchronizacji (np. domknięcie wizyt zakończonych na innym urządzeniu). */
  afterSync: () => Promise<void> = async () => {};
  /** Wołane po zmianie gospodarstwa (odświeżenie sesji i listy domowników). */
  onHouseholdChanged: (householdId: Id) => void = () => {};

  /** Identyfikator tego urządzenia w pushu i na sockecie (poke z własnym clientId pomijamy). */
  get clientId(): Id {
    return getDeviceId();
  }

  getStatus = () => this.status;

  subscribe = (fn: (s: SyncStatus) => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };

  private set(patch: Partial<SyncStatus>) {
    this.status = { ...this.status, ...patch };
    for (const fn of this.listeners) fn(this.status);
  }

  householdChanged(householdId: Id) {
    this.schedule(0);
    try {
      this.onHouseholdChanged(householdId);
    } catch (e) {
      console.error(e);
    }
  }

  /** Uruchamia nasłuch zdarzeń (online, powrót do aplikacji) i cykliczną synchronizację. */
  start() {
    if (this.started) return;
    this.started = true;
    window.addEventListener('online', () => {
      this.set({ online: true });
      this.schedule(0);
    });
    window.addEventListener('offline', () => this.set({ online: false }));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.schedule(0);
    });
    // Zapas na wypadek zerwanego socketu /api/live.
    setInterval(() => {
      if (document.visibilityState === 'visible') this.schedule(0);
    }, INTERVAL_MS);
    void this.refreshPending();
    this.schedule(0);
  }

  schedule(delayMs = 400) {
    void this.refreshPending();
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.run();
    }, delayMs);
  }

  /** Synchronizuje teraz; zwraca obietnicę zakończenia. */
  async run(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      do {
        this.again = false;
        await this.once();
      } while (this.again);
    })().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async refreshPending() {
    this.set({ pending: await db.outbox.count() });
  }

  private async once() {
    if (!navigator.onLine) {
      this.set({ online: false });
      return;
    }
    this.set({ syncing: true });
    try {
      try {
        await this.push();
        await this.pull();
      } catch (e) {
        // Dom właśnie scalany z innym: po chwili sesja wskaże już nowy dom.
        if (!(e instanceof HouseholdChangedError)) throw e;
        await sleep(HOUSEHOLD_RETRY_MS);
        await this.push();
        await this.pull();
      }
      this.set({ online: true, syncing: false, error: null, lastSyncMs: Date.now() });
      await this.afterSync();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        this.set({ syncing: false, error: 'unauthorized' });
        this.onUnauthorized();
        return;
      }
      // Błąd sieci albo serwera: zmiany czekają w outboxie na kolejną próbę.
      this.set({ syncing: false, error: e instanceof Error ? e.message : String(e) });
    } finally {
      await this.refreshPending();
    }
  }

  private async request<T>(input: string, init?: RequestInit): Promise<{ status: number; body: T | null }> {
    const res = await fetch(input, { credentials: 'same-origin', ...init });
    if (res.status === 401) throw new UnauthorizedError();
    const body = res.headers.get('content-type')?.includes('json') ? ((await res.json()) as T) : null;
    if (res.status === 409 && (body as { error?: string } | null)?.error === 'household_changed') {
      throw new HouseholdChangedError('household_changed');
    }
    return { status: res.status, body };
  }

  private async push() {
    const clientId = (await getMeta<string>('deviceId')) ?? 'unknown';
    for (;;) {
      const batch = await db.outbox.orderBy('seq').limit(PUSH_BATCH).toArray();
      if (batch.length === 0) return;
      const { status, body } = await this.request<PushResponse>('/api/sync/push', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ clientId, mutations: batch.map((b) => b.mutation) }),
      });
      if (status !== 200 || !body) throw new Error(`push: HTTP ${status}`);
      const rejected = body.results.filter((r) => r.status === 'rejected');
      if (rejected.length > 0) console.warn('Serwer odrzucił zmiany', rejected);
      // Zastosowane, duplikaty i odrzucone opuszczają outbox (odrzuconych nie da się naprawić ponowieniem).
      await db.outbox.bulkDelete(batch.map((b) => b.seq!));
      if (body.householdId) await adoptHousehold(body.householdId);
    }
  }

  private async pull() {
    let cursor = (await getMeta<number>('cursor')) ?? 0;
    for (;;) {
      const { status, body } = await this.request<PullResponse>(`/api/sync/pull?cursor=${cursor}`);
      if (status === 410) {
        cursor = 0;
        await setMeta('cursor', 0);
        continue;
      }
      if (status !== 200 || !body) throw new Error(`pull: HTTP ${status}`);
      const page = await db.transaction('rw', db.tables, async () => {
        let changedTo: Id | null = null;
        if (body.householdId && (await switchHousehold(body.householdId)) !== 'same') changedTo = body.householdId;
        // Kursor wyzerowany (zmiana domu teraz albo w trakcie, np. po odświeżeniu sesji) — strona
        // pobrana starym kursorem go nie przesuwa, zaczynamy od zera.
        if (((await getMeta<number>('cursor')) ?? 0) !== cursor) return { next: 0, changedTo };
        let maxHlc: string | null = null;
        for (const r of body.rows) {
          if (r.entity === 'orderEvents') {
            if (!(await db.orderEvents.get(r.data.id))) await db.orderEvents.put(r.data);
            if (!maxHlc || r.data.hlc > maxHlc) maxHlc = r.data.hlc;
            continue;
          }
          const table = lwwTable(r.entity);
          const local = (await table.get(r.data.id)) as LwwRow | undefined;
          await table.put(mergeRow(local, r.data) as never);
          const h = rowHlc(r.data);
          if (h && (!maxHlc || h > maxHlc)) maxHlc = h;
        }
        if (maxHlc) await observe(maxHlc);
        await setMeta('cursor', body.cursor);
        return { next: body.hasMore ? body.cursor : null, changedTo };
      });
      if (page.changedTo) this.householdChanged(page.changedTo);
      if (page.next === null) return;
      cursor = page.next;
    }
  }
}

export const syncEngine = new SyncEngine();
